import React, { useState, useEffect } from 'react';
import { Dialog } from 'radix-ui';
import { api } from '../services/api';
import { OrganizationUser } from '../types';
import { Trash2, UserCog, X, UserPlus } from 'lucide-react';

export function AdminUsers() {
  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ email: '', fullName: '', password: '', role: 'cashier' });

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try { const { users } = await api.getOrganizationUsers(); setUsers(users); }
    catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await api.addUser({ email: form.email, fullName: form.fullName, password: form.password, role: form.role });
      closeForm();
      loadUsers();
    } catch (err: any) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  const handleUpdateRole = async (userId: string, role: string) => {
    try { await api.updateUserRole(userId, role); loadUsers(); }
    catch (err: any) { setError(err.message); }
  };

  const handleRemove = async (userId: string) => {
    if (!confirm('Remove this user?')) return;
    try { await api.removeUser(userId); loadUsers(); }
    catch (err: any) { setError(err.message); }
  };

  const closeForm = () => {
    setShowForm(false);
    setForm({ email: '', fullName: '', password: '', role: 'cashier' });
    setError('');
  };

  if (loading) return <div className="flex items-center justify-center h-96 text-slate-400 text-lg">Loading...</div>;

  return (
    <div className="max-w-5xl mx-auto pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 sticky top-0 bg-[#f0f4f8]/80 backdrop-blur-md py-4 z-10 border-b border-transparent">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Team & Users</h1>
          <p className="text-slate-500 font-medium mt-1">{users.length} team members</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-5 py-3.5 bg-[#2563eb] text-white rounded-2xl font-bold hover:bg-[#1d4ed8] transition-all active:scale-95 shadow-md shadow-blue-900/10"
        >
          <UserPlus className="w-5 h-5" />
          Invite User
        </button>
      </div>

      {error && !showForm && (
        <div className="px-5 py-4 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-8 font-medium">
          {error}
        </div>
      )}

      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="px-8 py-5 text-[12px] font-bold text-slate-400 uppercase tracking-widest">Name</th>
                <th className="px-8 py-5 text-[12px] font-bold text-slate-400 uppercase tracking-widest">Email</th>
                <th className="px-8 py-5 text-[12px] font-bold text-slate-400 uppercase tracking-widest">Role</th>
                <th className="px-8 py-5 text-[12px] font-bold text-slate-400 uppercase tracking-widest">Joined</th>
                <th className="px-8 py-5 text-right text-[12px] font-bold text-slate-400 uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="group border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors">
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-full bg-blue-50 text-[#1e40af] border border-blue-100 flex items-center justify-center font-bold shadow-sm">
                        {user.full_name.charAt(0)}
                      </div>
                      <span className="font-bold text-slate-800 text-[16px]">{user.full_name}</span>
                    </div>
                  </td>
                  <td className="px-8 py-5 text-[15px] font-medium text-slate-500">{user.email}</td>
                  <td className="px-8 py-5">
                    <select
                      value={user.role}
                      onChange={e => handleUpdateRole(user.id, e.target.value)}
                      className={`
                        py-1.5 px-3 rounded-full text-[13px] font-bold appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-slate-200 text-center uppercase tracking-wide
                        ${user.role === 'owner' ? 'bg-amber-50 text-amber-700 border border-amber-200/50' : ''}
                        ${user.role === 'manager' ? 'bg-blue-50 text-[#1e40af] border border-blue-200/50' : ''}
                        ${user.role === 'cashier' ? 'bg-slate-100 text-slate-600 border border-slate-200' : ''}
                      `}
                    >
                      <option value="cashier">Cashier</option>
                      <option value="manager">Manager</option>
                      <option value="owner">Owner</option>
                    </select>
                  </td>
                  <td className="px-8 py-5 text-[14px] font-medium text-slate-500">{new Date(user.joined_at).toLocaleDateString()}</td>
                  <td className="px-8 py-5 text-right">
                    <button
                      onClick={() => handleRemove(user.id)}
                      className="inline-flex items-center justify-center p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                      title="Remove User"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role legend */}
      <div className="mt-8 p-6 bg-amber-50 border border-amber-200 rounded-2xl">
        <h4 className="font-semibold text-slate-900 mb-2">User Permissions</h4>
        <p className="text-sm text-slate-600 leading-relaxed">
          <strong>Owner:</strong> Full access to settings, products, users, and Stripe<br />
          <strong>Manager:</strong> Manages products and store settings<br />
          <strong>Cashier:</strong> Can process sales and view inventory
        </p>
      </div>

      {/* Add User Modal */}
      <Dialog.Root open={showForm} onOpenChange={(open) => { if (!open) closeForm(); else setShowForm(true); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-[2rem] shadow-2xl p-8 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
            <Dialog.Title className="text-3xl font-extrabold text-slate-900 tracking-tight mb-2">Invite User</Dialog.Title>
            <Dialog.Description className="text-slate-500 font-medium mb-8">
              Add a new team member to your workspace
            </Dialog.Description>

            {error && (
              <div className="px-4 py-3 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-6 text-sm font-medium">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Jane Doe"
                    value={form.fullName}
                    onChange={e => setForm({ ...form, fullName: e.target.value })}
                    required
                    className="w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="jane@example.com"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    required
                    className="w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    placeholder="Temporary password"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    required
                    minLength={6}
                    className="w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Role</label>
                  <div className="relative">
                    <select
                      value={form.role}
                      onChange={e => setForm({ ...form, role: e.target.value })}
                      className="w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all appearance-none pr-10 cursor-pointer"
                    >
                      <option value="cashier">Cashier — operates POS</option>
                      <option value="manager">Manager — manages products</option>
                      <option value="owner">Owner — full access</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-slate-400">
                      <UserCog className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={submitting}
                  className="flex-1 h-14 px-6 bg-slate-100 text-slate-600 rounded-2xl font-bold hover:bg-slate-200 transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`flex-1 h-14 px-6 rounded-2xl font-bold transition-all active:scale-[0.98] shadow-lg ${
                    submitting
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed shadow-none'
                      : 'bg-[#2563eb] text-white hover:bg-[#1d4ed8] shadow-blue-900/20'
                  }`}
                >
                  {submitting ? 'Adding…' : 'Add User'}
                </button>
              </div>
            </form>

            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="absolute top-6 right-6 w-10 h-10 rounded-full flex items-center justify-center hover:bg-slate-100 active:scale-95 transition-all"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
